// Badges par parcours, avantages par grade (D-100). Remplace les points, les niveaux et les badges à
// règles de D-041 et D-044. Tout se calcule à partir des MISSIONS (grand livre de la base,
// `_mission_ledger`) : un créneau tenu = 1 mission, rangée dans un parcours d'après son nom ; une semaine
// à tous les entraînements = 1 mission d'Assiduité ; une recrue, une entreprise présentée = 1 mission
// d'Ambassade (3 pour un partenaire signé) ; une étoile donnée par le bureau = 1 mission.
//
// Échelle commune à tous les parcours : En herbe dès la 1re mission, Bronze dès 5, Argent dès 15, Or dès 30,
// Légende dès 50. Dans un grade, 5 étoiles = le grade suivant ; une étoile vaut 1 mission en herbe, 2 en
// Bronze, 3 en Argent, 4 en Or. Les étoiles ne se dépensent pas, ne baissent jamais, se cumulent d'une saison
// à l'autre. La base applique les mêmes règles (`_grade_of`, `_parcours_auto`) : le banc les vérifie.
//
// D-127 : le club règle ses parcours (Club › Bénévolat › Parcours) : ceux de base se renomment et se masquent (masqué :
// plus affiché nulle part, les étoiles restent), le club crée les siens (nom, picto, créneaux qui comptent, une phrase).
// Ici, le registre du club affiché (setClubParcours) : parcoursOf, les listes de parcours et les totaux le suivent.

/** Les parcours de base, nommés par l'activité (sans genre). `slot` : un créneau peut y être rangé ; `blurb` : ce qui y
 *  compte, en une phrase (les réglages du club, D-127). */
export const PARCOURS = [
  { key: 'arbitrage', name: 'Arbitrage', picto: 'arbitrage', slot: true, blurb: 'Arbitrer les matchs du club, des plus petits aux seniors' },
  { key: 'table', name: 'Table de marque', picto: 'table_marque', slot: true, blurb: 'Table de marque, chrono, 24 secondes' },
  { key: 'buvette', name: 'Buvette et cuisine', picto: 'buvette', slot: true, blurb: 'Buvette, gâteaux, repas des matchs et des fêtes' },
  { key: 'logistique', name: 'Logistique', picto: 'installation', slot: true, blurb: 'Installer, ranger, laver les maillots' },
  { key: 'transport', name: 'Transport', picto: 'accompagnateur', slot: true, blurb: 'Conduire les équipes aux matchs à l’extérieur' },
  { key: 'evenements', name: 'Événements', picto: 'tournoi', slot: true, blurb: 'Tournois, lotos, fêtes du club' },
  { key: 'encadrement', name: 'Encadrement', picto: 'ballon', slot: true, blurb: 'Aider les coachs, parrainer les jeunes' },
  { key: 'ambassade', name: 'Ambassade', picto: 'communication', slot: false, blurb: 'Une recrue, une entreprise présentée, un partenaire signé' },
  { key: 'assiduite', name: 'Assiduité', picto: 'assiduite', slot: false, blurb: 'Une semaine à tous les entraînements' },
  { key: 'coup_de_main', name: 'Coup de main', picto: 'etoile', slot: true, blurb: 'Les créneaux sans parcours, les étoiles du bureau' },
] as const;
/** Un parcours : de base (« arbitrage »…) ou créé par le club (« p_ » et 10 chiffres hexadécimaux, D-127). */
export type ParcoursKey = string;
export type BaseParcoursKey = typeof PARCOURS[number]['key'];
const BASE_KEYS: string[] = PARCOURS.map(p => p.key);
/** Les parcours de base que le club renomme ou masque (D-127) : pas l'Assiduité (réglée à part), pas le Coup de main. */
export const SETTABLE_PARCOURS = ['arbitrage', 'table', 'buvette', 'logistique', 'transport', 'evenements', 'encadrement', 'ambassade'];

// -----------------------------------------------------------------------------
// Les parcours du club (D-127) : renommés, masqués, créés. Lus une fois par club (badge_parcours), puis partout.
// -----------------------------------------------------------------------------
/** Une ligne réglée par le club (volunteer_parcours). */
export interface ClubParcoursRow { key: string; name: string | null; picto: string | null; blurb: string | null; hidden: boolean; custom: boolean }
/** Un parcours tel que le club l'affiche. `origin` : le nom d'origine (parcours de base). */
export interface ParcoursDef { key: string; name: string; origin: string; picto: string; slot: boolean; custom: boolean; hidden: boolean; blurb: string }

let CLUB: ClubParcoursRow[] = [];
/** Les réglages du club affiché (src/lib/reglages-api.ts, ensureParcours). Vide : les parcours de base, tels quels. */
export function setClubParcours(rows: ClubParcoursRow[]): void { CLUB = rows.slice(); }

/** Un parcours (inconnu : le Coup de main), sous le nom que lui donne le club. */
export function parcoursOf(k: string): ParcoursDef {
  const base = PARCOURS.find(p => p.key === k);
  const row = CLUB.find(r => r.key === k);
  if (base) return { key: base.key, name: row?.name?.trim() || base.name, origin: base.name, picto: base.picto, slot: base.slot, custom: false, hidden: !!row?.hidden, blurb: base.blurb };
  if (row?.custom) return { key: row.key, name: row.name?.trim() || 'Parcours', origin: row.name?.trim() || '', picto: row.picto || 'etoile', slot: true, custom: true, hidden: row.hidden, blurb: row.blurb?.trim() || '' };
  return parcoursOf('coup_de_main');
}
export const isParcours = (k: string): boolean => BASE_KEYS.includes(k) || CLUB.some(r => r.custom && r.key === k);
/** Tous les parcours du club, masqués compris : ceux de base, les créés après Événements (ceux qu'on tient sur un créneau). */
export function allParcours(): ParcoursDef[] {
  const created = CLUB.filter(r => r.custom).map(r => r.key);
  const at = BASE_KEYS.indexOf('encadrement');
  return [...BASE_KEYS.slice(0, at), ...created, ...BASE_KEYS.slice(at)].map(parcoursOf);
}
/** Les parcours affichés (D-127 : masqué, un parcours ne s'affiche plus nulle part ; ses étoiles restent). */
export const visibleParcours = (): string[] => allParcours().filter(p => !p.hidden).map(p => p.key);
/** Les parcours proposés pour « Donner une étoile » (maquette 09) : ceux qu'on découvre, sans le Coup de main. */
export const starParcours = (): string[] => visibleParcours().filter(k => k !== 'assiduite' && k !== 'coup_de_main');
/** Les parcours où l'on range un nom de créneau (le bureau) : ceux des créneaux, Coup de main compris. */
export const slotParcours = (): string[] => allParcours().filter(p => p.slot && !p.hidden).map(p => p.key);
/** Les parcours de la page « Parcours » de Club › Bénévolat (maquette r2) : la table d'abord, ceux du club avant
 *  l'Encadrement ; ni l'Assiduité (« Annonces et relances »), ni le Coup de main. */
export function settingsParcours(): ParcoursDef[] {
  const order = ['table', 'arbitrage', 'buvette', 'logistique', 'transport', 'evenements'];
  return [...order, ...CLUB.filter(r => r.custom).map(r => r.key), 'encadrement', 'ambassade'].map(parcoursOf);
}

export const GRADES = [
  { key: 'herbe', name: 'En herbe', min: 1, per: 1 },
  { key: 'bronze', name: 'Bronze', min: 5, per: 2 },
  { key: 'argent', name: 'Argent', min: 15, per: 3 },
  { key: 'or', name: 'Or', min: 30, per: 4 },
  { key: 'legende', name: 'Légende', min: 50, per: 0 },
] as const;
export type GradeKey = typeof GRADES[number]['key'];
export const gradeInfo = (k: GradeKey) => GRADES.find(g => g.key === k)!;
export const gradeRank = (k: GradeKey | 'off') => (k === 'off' ? -1 : GRADES.findIndex(g => g.key === k));

export interface Grade {
  /** 'off' : pas encore de mission dans ce parcours. */
  key: GradeKey | 'off';
  name: string;
  /** Étoiles pleines dans le grade (0 à 4 ; 5 en Légende). */
  stars: number;
  /** Missions avant la prochaine étoile (0 en Légende). */
  toStar: number;
  next: { key: GradeKey; name: string; min: number } | null;
  /** Missions avant le grade suivant (0 en Légende). */
  toNext: number;
  /** Avancée dans le grade, de 0 à 100 (vers le grade suivant). */
  pct: number;
}

/** Le grade d'un parcours d'après son nombre de missions. */
export function gradeOf(missions: number): Grade {
  const m = Math.max(0, Math.floor(missions));
  if (m < 1) return { key: 'off', name: 'À découvrir', stars: 0, toStar: 1, next: { key: 'herbe', name: 'En herbe', min: 1 }, toNext: 1, pct: 0 };
  let i = 0;
  GRADES.forEach((g, j) => { if (m >= g.min) i = j; });
  const g = GRADES[i]!, nx = GRADES[i + 1];
  if (!nx) return { key: g.key, name: g.name, stars: 5, toStar: 0, next: null, toNext: 0, pct: 100 };
  // En herbe : la 1re mission donne la 1re étoile (1 à 4 étoiles) ; ailleurs, on entre à 0 étoile.
  const stars = Math.min(4, Math.floor((m - g.min) / g.per) + (g.key === 'herbe' ? 1 : 0));
  const toStar = g.key === 'herbe' ? 1 : g.per - ((m - g.min) % g.per);
  return {
    key: g.key, name: g.name, stars, toStar, next: { key: nx.key, name: nx.name, min: nx.min }, toNext: nx.min - m,
    pct: Math.max(0, Math.min(100, Math.round(((m - g.min) / (nx.min - g.min)) * 100))),
  };
}

/** Le mot d'une mission dans un parcours : une semaine (Assiduité), une mission ailleurs. */
export const unitOf = (p: string, n: number) => (p === 'assiduite' ? (n > 1 ? 'semaines' : 'semaine') : n > 1 ? 'missions' : 'mission');
export const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** « 9 missions avant Or », « Une mission pour commencer », « Grade maximum : merci ! » (tuile, maquette 02). */
export function toNextText(p: string, missions: number): string {
  const g = gradeOf(missions);
  if (!g.next) return 'Grade maximum : merci !';
  if (g.key === 'off') return p === 'ambassade' ? 'Une recrue ou une entreprise présentée' : p === 'assiduite' ? 'Une semaine complète pour commencer' : 'Une mission pour commencer';
  return `${g.toNext} ${unitOf(p, g.toNext)} avant ${g.next.name}`;
}

/** « encore 2 matchs » (prochain cap, maquette 01) : des matchs à la table et à l'arbitrage, des semaines en Assiduité. */
export function capText(p: string, missions: number): string {
  const n = gradeOf(missions).toNext;
  const w = p === 'table' || p === 'arbitrage' ? (n > 1 ? 'matchs' : 'match') : unitOf(p, n);
  return `encore ${n} ${w}`;
}

/** « 5 matchs à la table : le club te dit merci, Sarah ! » (fête du passage de grade, maquette 04). */
export function cheerLine(p: string, missions: number, name: string, vous = false): string {
  const n = Math.max(1, missions);
  const what = p === 'table' ? `${plural(n, 'match', 'matchs')} à la table`
    : p === 'arbitrage' ? `${plural(n, 'match arbitré', 'matchs arbitrés')}`
    : p === 'assiduite' ? `${plural(n, 'semaine', 'semaines')} à tous les entraînements`
    : p === 'ambassade' ? `${plural(n, 'mission', 'missions')} d’ambassade`
    : `${plural(n, 'mission', 'missions')} en ${parcoursOf(p).name}`;
  return `${what} : le club ${vous ? 'vous' : 'te'} dit merci${name ? `, ${name}` : ''} !`;
}

// -----------------------------------------------------------------------------
// Un créneau rangé dans son parcours d'après son nom (le bureau peut le ranger autrement)
// -----------------------------------------------------------------------------

/** Le nom d'un créneau, comparable : minuscules, sans accents ni ponctuation (« E-marque » = « e marque »). */
export function labelKey(label: string): string {
  return label.toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
}

// Mots qui rangent un créneau ; l'ordre compte (« Arbitrage du plateau » : Arbitrage avant Événements,
// « Installation des tables » : Logistique, jamais Table de marque). Mêmes motifs que `_parcours_auto` en base.
const RULES: [BaseParcoursKey, RegExp][] = [
  ['arbitrage', /\barbitr|\bsifflet/],
  ['table', /\btables? de marque\b|^tables?$|\be ?marque\b|\bchrono|\b24 ?(s|sec|secondes)\b|\bfeuille de match\b|\bmarqueur|\bscoreur|\bofficiel/],
  ['buvette', /\bbuvette|\bcuisin|\bcrepe|\bgateau|\bpatiss|\bbar\b|\bsnack|\brepas\b|\bsandwich|\bcafe\b|\bboisson|\bbarbecue|\bbbq\b|\bgouter|\bfrites?\b|\bhot ?dog|\btraiteur/],
  ['logistique', /\binstall|\brangement|\branger\b|\blavage|\blaver\b|\bmaillots?\b|\bmontage|\bdemontage|\bmateriel|\bnettoyage|\bmenage|\bentretien|\bchaises?\b|\btables\b|\bvestiaire|\bparking|\blogisti|\bamenagement|\bbricolage/],
  ['transport', /\btransport|\bchauffeur|\bconduct|\bcovoit|\bminibus|\bnavette|\bvoiture|\bdeplacement|\btrajet|\bbus\b/],
  ['encadrement', /\bencadr|\baccompagn|\baide ?coach|\bassistant|\bentraine|\becole de basket|\bbaby ?basket|\bmini ?basket|\beducat|\bcoach|\bresponsable d ?equipe|\bdelegue|\bparrain|\bstage\b/],
  ['evenements', /\btournoi|\bloto\b|\bgala\b|\bfete\b|\bsoiree|\btombola|\banimation|\bbilletterie|\bentree\b|\bcaisse\b|\baccueil|\bstand\b|\bvide ?grenier|\bkermesse|\bassemblee|\bceremonie|\bnoel\b|\bcarnaval|\bplateau|\bevenement|\bconcours\b/],
];

/** Le parcours d'un nom de créneau, d'après ses mots ; inconnu : « Coup de main ». */
export function autoParcours(label: string): BaseParcoursKey {
  const k = labelKey(label);
  return RULES.find(([, re]) => re.test(k))?.[0] ?? 'coup_de_main';
}

/** Le parcours d'un créneau : celui que le bureau a choisi pour ce nom, sinon d'après ses mots. */
export function parcoursOfLabel(label: string, overrides: Map<string, string> | Record<string, string> = {}): ParcoursKey {
  const k = labelKey(label);
  const o = overrides instanceof Map ? overrides.get(k) : overrides[k];
  return o && isParcours(o) ? o : autoParcours(label);
}

// -----------------------------------------------------------------------------
// Mes missions, par parcours
// -----------------------------------------------------------------------------

/** Une mission du grand livre (my_missions). `missions` : 1, ou 3 pour un partenaire signé. */
export interface MissionRow { day: string; parcours: string; label: string; title: string; source: string; missions: number }

export interface ParcoursState { key: ParcoursKey; name: string; missions: number; season: number; grade: Grade; last: string | null }

/** Le total de chaque parcours affiché (tous, même à 0 ; pas un parcours masqué, D-127), du plus avancé au moins avancé. */
export function parcoursTotals(rows: MissionRow[], season: { from: string; to: string } | null = null): ParcoursState[] {
  const keys = visibleParcours();
  return keys.map(key => {
    const mine = rows.filter(r => r.parcours === key);
    const missions = mine.reduce((a, r) => a + r.missions, 0);
    const season_ = season ? mine.filter(r => r.day >= season.from && r.day <= season.to).reduce((a, r) => a + r.missions, 0) : missions;
    const last = mine.reduce<string | null>((m, r) => (!m || r.day > m ? r.day : m), null);
    return { key, name: parcoursOf(key).name, missions, season: season_, grade: gradeOf(missions), last };
  }).sort((a, b) => b.missions - a.missions || keys.indexOf(a.key) - keys.indexOf(b.key));
}

/** Les missions du bénévolat (sans l'Assiduité) : le total « au club » et la saison. */
export function volunteerTotal(rows: MissionRow[], season: { from: string; to: string } | null = null): { all: number; season: number } {
  const v = rows.filter(r => r.parcours !== 'assiduite');
  return {
    all: v.reduce((a, r) => a + r.missions, 0),
    season: season ? v.filter(r => r.day >= season.from && r.day <= season.to).reduce((a, r) => a + r.missions, 0) : v.reduce((a, r) => a + r.missions, 0),
  };
}

/** « Buvette Argent · Logistique Bronze · 2 autres » (bande « Mes badges »). */
export function badgesLine(started: ParcoursState[], max = 2): string {
  const shown = started.slice(0, max).map(p => `${p.name.replace(/ et cuisine$/, '')} ${p.grade.name}`);
  const more = started.length - shown.length;
  return [...shown, ...(more > 0 ? [`${more} ${more > 1 ? 'autres' : 'autre'}`] : [])].join(' · ');
}

// -----------------------------------------------------------------------------
// Le prochain cap : le parcours le plus près de son grade suivant, relié à un vrai créneau libre
// -----------------------------------------------------------------------------

export interface OpenSlotLike { slot_id: string; label: string; occurrence_date: string }

/**
 * Le prochain cap (accueil Bénévole, carte de bénévolat des jeunes) : parmi les parcours commencés du bénévolat
 * (pas l'Assiduité, pas la Légende), celui qui a le moins de missions avant son grade suivant ; à égalité, celui
 * qui a un créneau libre. Le créneau proposé : le premier créneau libre de ce parcours. Rien de commencé : le
 * parcours du premier créneau libre (« Une mission pour commencer »). Rien du tout : null.
 */
export function nextCap<T extends OpenSlotLike>(states: ParcoursState[], open: T[], overrides: Map<string, string> = new Map()):
  { parcours: ParcoursState; slot: T | null } | null {
  const slotOf = (p: string) => open.find(s => parcoursOfLabel(s.label, overrides) === p) ?? null;
  const started = states.filter(s => s.missions > 0 && s.key !== 'assiduite' && s.grade.next);
  const ranked = [...started].sort((a, b) => a.grade.toNext - b.grade.toNext || Number(!!slotOf(b.key)) - Number(!!slotOf(a.key)) || b.missions - a.missions);
  const best = ranked[0];
  if (best) return { parcours: best, slot: slotOf(best.key) };
  // Un créneau d'un parcours masqué (D-127) n'est jamais un cap : son parcours ne s'affiche plus.
  const shown = open.filter(s => !parcoursOf(parcoursOfLabel(s.label, overrides)).hidden);
  const first = shown.find(s => parcoursOfLabel(s.label, overrides) !== 'coup_de_main') ?? shown[0];
  if (!first) return null;
  const p = parcoursOfLabel(first.label, overrides);
  return { parcours: states.find(s => s.key === p) ?? { key: p, name: parcoursOf(p).name, missions: 0, season: 0, grade: gradeOf(0), last: null }, slot: first };
}

// -----------------------------------------------------------------------------
// Les récompenses des grades : une par grade et par parcours
// -----------------------------------------------------------------------------

/** La récompense d'un grade (réglée par le bureau). `perk` : un cadeau à utiliser ; sinon, un merci dans l'app. */
export interface GradeReward { grade: GradeKey; picto: string; title: string; perk: boolean; parcours: string[] | null }

/** Les récompenses par défaut (D-100) : le bureau ne règle que celles-ci. */
export const DEFAULT_REWARDS: GradeReward[] = [
  { grade: 'herbe', picto: 'coeur', title: 'Un merci dans l’app', perk: false, parcours: null },
  { grade: 'bronze', picto: 'cafe', title: 'Boisson offerte à la buvette', perk: true, parcours: null },
  { grade: 'argent', picto: 'cadeau', title: 'Gourde du club', perk: true, parcours: null },
  { grade: 'or', picto: 'vip', title: 'Place au premier rang du gala', perk: true, parcours: null },
  { grade: 'legende', picto: 'maillot', title: 'Maillot du club floqué', perk: true, parcours: null },
];

/** La récompense de ce grade s'applique-t-elle à ce parcours ? (le bureau peut la réserver à certains parcours) */
export const rewardFor = (r: GradeReward, p: string) => !r.parcours?.length || r.parcours.includes(p);

export interface RewardUse { grade: string; parcours: string; used_at: string }

/** Mes récompenses gagnées : chaque grade atteint dans un parcours, s'il donne un cadeau, avec son utilisation. */
export function earnedRewards(states: ParcoursState[], rewards: GradeReward[], uses: RewardUse[]):
  { reward: GradeReward; parcours: ParcoursState; used_at: string | null }[] {
  const out: { reward: GradeReward; parcours: ParcoursState; used_at: string | null }[] = [];
  for (const s of states) {
    for (const r of rewards) {
      if (!r.perk || !rewardFor(r, s.key) || s.missions < gradeInfo(r.grade).min) continue;
      const u = uses.find(x => x.grade === r.grade && x.parcours === s.key);
      out.push({ reward: r, parcours: s, used_at: u?.used_at ?? null });
    }
  }
  return out.sort((a, b) => gradeRank(b.reward.grade) - gradeRank(a.reward.grade) || b.parcours.missions - a.parcours.missions);
}

/**
 * « À débloquer » : pour chaque récompense à utiliser, le parcours commencé le plus proche de ce grade sans l'avoir
 * atteint (« Le plus proche : Table de marque, encore 10 missions »). Aucun parcours commencé : « dans n'importe
 * quel parcours ». Chaque grade une seule fois. `held` : les grades dont la récompense m'attend déjà (« Tes
 * récompenses ») — elle n'est pas aussi « à débloquer » (D-103).
 */
export function toUnlock(states: ParcoursState[], rewards: GradeReward[], held: ReadonlySet<GradeKey> = new Set()):
  { reward: GradeReward; parcours: ParcoursState | null; toGo: number; pct: number }[] {
  return rewards.filter(r => r.perk && !held.has(r.grade)).flatMap(r => {
    const min = gradeInfo(r.grade).min;
    const cands = states.filter(s => s.missions > 0 && s.missions < min && rewardFor(r, s.key)).sort((a, b) => b.missions - a.missions);
    const best = cands[0] ?? null;
    // Déjà atteint partout où l'on est actif : rien à débloquer de ce côté (un nouveau parcours le redonnera).
    if (!best && states.some(s => s.missions > 0)) return [];
    return [{ reward: r, parcours: best, toGo: best ? min - best.missions : min, pct: best ? Math.round((best.missions / min) * 100) : 0 }];
  });
}

// -----------------------------------------------------------------------------
// Palmarès des défis : les médailles des défis clos où l'on est monté sur le podium
// -----------------------------------------------------------------------------

export type Medal = 'or' | 'argent' | 'bronze';
export const MEDAL_LABEL: Record<Medal, string> = { or: 'Or', argent: 'Argent', bronze: 'Bronze' };
export const medalOfRank = (rank: number): Medal | null => (rank === 1 ? 'or' : rank === 2 ? 'argent' : rank === 3 ? 'bronze' : null);
export const medalCounts = (list: { medal: Medal }[]): Record<Medal, number> =>
  ({ or: list.filter(m => m.medal === 'or').length, argent: list.filter(m => m.medal === 'argent').length, bronze: list.filter(m => m.medal === 'bronze').length });
